import { useEffect, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useProfileQuery, useUpsertProfile } from "../hooks/useNameProfiles";
import { computeAgeRange } from "../utils/ageRange";
import {
  SHIRT_SIZES,
  PANTS_SIZES,
  PANTS_CATEGORIES,
  MENS_WAIST_SIZES,
  MENS_INSEAM_SIZES,
  WOMENS_PANTS_SIZES,
  SHOE_SIZES,
} from "../utils/profileOptions";
import GoHomeButton from "./GoHomeButton";

const EMPTY_FORM = {
  birthdate: "",
  shirtSize: "",
  pantsCategory: "",
  pantsSize: "",
  mensWaist: "",
  mensInseam: "",
  shoeSize: "",
  favoriteBrands: [],
  interests: [],
  notes: "",
};

// Single-size list for each non-men's pants category (men's uses waist + inseam).
const PANTS_SIZES_BY_CATEGORY = { women: WOMENS_PANTS_SIZES, letter: PANTS_SIZES };

// options: plain strings, or { value, label } objects when the stored value and
// the displayed text differ.
function SizeSelect({ id, label, value, options, onChange }) {
  return (
    <FormControl fullWidth>
      <InputLabel id={`${id}-label`}>{label}</InputLabel>
      <Select
        labelId={`${id}-label`}
        label={label}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      >
        <MenuItem value="">
          <em>Not specified</em>
        </MenuItem>
        {options.map((opt) => {
          const optValue = typeof opt === "string" ? opt : opt.value;
          return (
            <MenuItem key={optValue} value={optValue}>
              {typeof opt === "string" ? opt : opt.label}
            </MenuItem>
          );
        })}
      </Select>
    </FormControl>
  );
}

export default function ProfileEdit({ userId, childNameId, label, onBack, onNavigateHome }) {
  const { data: profile, isLoading } = useProfileQuery({ userId, childNameId });
  const upsertMutation = useUpsertProfile();

  const [form, setForm] = useState(EMPTY_FORM);
  const [error, setError] = useState(null);

  useEffect(() => {
    // undefined = still loading; null = no row yet (leave the empty form).
    if (profile === undefined) return;
    const pantsCategory = profile?.pants_size_category ?? "";
    const storedPants = profile?.pants_size ?? "";
    // Men's sizes are stored combined as waist x inseam, e.g. "36x30".
    const [mensWaist = "", mensInseam = ""] = pantsCategory === "men" ? storedPants.split("x") : [];
    setForm({
      birthdate: profile?.birthdate ?? "",
      shirtSize: profile?.shirt_size ?? "",
      pantsCategory,
      pantsSize: pantsCategory && pantsCategory !== "men" ? storedPants : "",
      mensWaist,
      mensInseam,
      shoeSize: profile?.shoe_size ?? "",
      favoriteBrands: profile?.favorite_brands ?? [],
      interests: profile?.interests ?? [],
      notes: profile?.notes ?? "",
    });
  }, [profile]);

  const setField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  // Switching category clears the previous category's sizes so a stale value
  // can't be saved under the wrong scheme.
  const setPantsCategory = (value) =>
    setForm((prev) => ({ ...prev, pantsCategory: value, pantsSize: "", mensWaist: "", mensInseam: "" }));

  const mensSizeIncomplete =
    form.pantsCategory === "men" && !!form.mensWaist !== !!form.mensInseam;

  const handleSave = async () => {
    setError(null);
    let pantsSize = "";
    if (form.pantsCategory === "men") {
      if (form.mensWaist && form.mensInseam) pantsSize = `${form.mensWaist}x${form.mensInseam}`;
    } else if (form.pantsCategory) {
      pantsSize = form.pantsSize;
    }
    try {
      await upsertMutation.mutateAsync({
        userId,
        childNameId,
        birthdate: form.birthdate,
        shirtSize: form.shirtSize,
        pantsSize,
        pantsSizeCategory: form.pantsCategory,
        shoeSize: form.shoeSize,
        favoriteBrands: form.favoriteBrands,
        interests: form.interests,
        notes: form.notes,
      });
      onBack();
    } catch {
      // Error already logged in nameProfilesService; keep the form populated for retry.
      setError("Something went wrong saving this profile. Please try again.");
    }
  };

  const agePreview = computeAgeRange(form.birthdate) ?? "—";

  return (
    <Paper elevation={2} className="page-card" sx={{ p: 3, mt: 2, mb: 4 }}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        spacing={2}
        sx={{ mb: 3 }}
      >
        <Typography variant="h5" className="section-header">
          {label ? `${label}'s Profile` : "Your Profile"}
        </Typography>
        <GoHomeButton onNavigateHome={onNavigateHome} />
      </Stack>

      {isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Stack spacing={3}>
          {error && <Alert severity="error">{error}</Alert>}

          <Box>
            <TextField
              label="Date of birth"
              type="date"
              value={form.birthdate}
              onChange={(e) => setField("birthdate", e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
            <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mt: 0.5 }}>
              Private — the rest of your group only ever sees an age range, never the date.
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              Will display to others as: <strong>{agePreview}</strong>
            </Typography>
          </Box>

          <SizeSelect
            id="shirt-size"
            label="Shirt size"
            value={form.shirtSize}
            options={SHIRT_SIZES}
            onChange={(v) => setField("shirtSize", v)}
          />
          <SizeSelect
            id="pants-category"
            label="Pants size category"
            value={form.pantsCategory}
            options={PANTS_CATEGORIES}
            onChange={setPantsCategory}
          />
          {form.pantsCategory === "men" && (
            <Box>
              <Stack direction="row" spacing={2}>
                <SizeSelect
                  id="mens-waist"
                  label="Waist"
                  value={form.mensWaist}
                  options={MENS_WAIST_SIZES}
                  onChange={(v) => setField("mensWaist", v)}
                />
                <SizeSelect
                  id="mens-inseam"
                  label="Inseam"
                  value={form.mensInseam}
                  options={MENS_INSEAM_SIZES}
                  onChange={(v) => setField("mensInseam", v)}
                />
              </Stack>
              {mensSizeIncomplete && (
                <FormHelperText>Pick both waist and inseam to save a men's size</FormHelperText>
              )}
            </Box>
          )}
          {PANTS_SIZES_BY_CATEGORY[form.pantsCategory] && (
            <SizeSelect
              id="pants-size"
              label="Pants size"
              value={form.pantsSize}
              options={PANTS_SIZES_BY_CATEGORY[form.pantsCategory]}
              onChange={(v) => setField("pantsSize", v)}
            />
          )}
          <SizeSelect
            id="shoe-size"
            label="Shoe size (US)"
            value={form.shoeSize}
            options={SHOE_SIZES}
            onChange={(v) => setField("shoeSize", v)}
          />

          <Autocomplete
            multiple
            freeSolo
            options={[]}
            value={form.favoriteBrands}
            onChange={(_e, value) => setField("favoriteBrands", value)}
            renderInput={(params) => (
              <TextField {...params} label="Favorite brands" placeholder="Type and press Enter" />
            )}
          />

          <Autocomplete
            multiple
            freeSolo
            options={[]}
            value={form.interests}
            onChange={(_e, value) => setField("interests", value)}
            renderInput={(params) => (
              <TextField {...params} label="Interests" placeholder="Type and press Enter" />
            )}
          />

          <TextField
            label="Notes"
            value={form.notes}
            onChange={(e) => setField("notes", e.target.value)}
            multiline
            minRows={3}
            fullWidth
          />

          <Stack direction="row" spacing={2}>
            <Button variant="contained" onClick={handleSave} disabled={upsertMutation.isPending}>
              {upsertMutation.isPending ? "Saving..." : "Save"}
            </Button>
            <Button variant="outlined" onClick={onBack}>
              Cancel
            </Button>
          </Stack>
        </Stack>
      )}
    </Paper>
  );
}
